import { useState } from 'react'
import { Search, X } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select-radix'
import { useDebounce } from '@/hooks/useDebounce'
import { useBonificacaoStore } from '@/stores/bonificacao-store'
import type { BonusAssessmentSummary } from '@/api/bonificacao-hooks'

interface CollaboratorFiltersProps {
  assessments: BonusAssessmentSummary[]
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'rascunho', label: 'Rascunho' },
  { value: 'em_apuracao', label: 'Em Apuração' },
  { value: 'validacao_rh', label: 'Validação RH' },
  { value: 'finalizado', label: 'Finalizado' },
]

export function CollaboratorFilters({ assessments }: CollaboratorFiltersProps) {
  const setFilters = useBonificacaoStore((s) => s.setFilters)
  const filterDepartment = useBonificacaoStore((s) => s.filterDepartment)
  const filterStatus = useBonificacaoStore((s) => s.filterStatus)
  const filterSearch = useBonificacaoStore((s) => s.filterSearch)

  const [searchInput, setSearchInput] = useState(filterSearch)
  const debouncedSearch = useDebounce(searchInput, 300)

  // Sync debounced search to store
  if (debouncedSearch !== filterSearch) {
    setFilters({ search: debouncedSearch })
  }

  const departments = [
    ...new Set(
      assessments
        .map((a) => a.department)
        .filter((d): d is string => !!d)
    ),
  ].sort()

  return (
    <div className="card mb-6 p-4" data-testid="bonificacao-filters">
      <div className="flex flex-col sm:flex-row gap-4">
        <Select
          value={filterDepartment || 'all'}
          onValueChange={(value) =>
            setFilters({ department: value === 'all' ? '' : value })
          }
        >
          <SelectTrigger className="sm:w-48" data-testid="filter-department">
            <SelectValue placeholder="Departamento" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {departments.map((dept) => (
              <SelectItem key={dept} value={dept}>
                {dept}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filterStatus || 'all'}
          onValueChange={(value) =>
            setFilters({ status: value === 'all' ? '' : value })
          }
        >
          <SelectTrigger className="sm:w-48" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray" />
          <input
            type="text"
            className="form-input pl-10 pr-10 w-full"
            placeholder="Buscar por nome ou cargo..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            data-testid="filter-search"
          />
          {searchInput && (
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-gray hover:text-white"
              onClick={() => {
                setSearchInput('')
                setFilters({ search: '' })
              }}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
